/**
 * UnifiedMentionPicker
 *
 * Renders one mention picker for Losi, members, assistants, and exposed
 * Super Agents.
 *
 * Primary responsibilities:
 * - Filter typed @ mention options
 * - Render platform, exposed Super Agent, human, and assistant rows
 * - Keep assistant plan validation before selection
 *
 * Dependencies:
 * - Chatbot UI assistant metadata
 * - Assistant plan validation
 */

import { useCallback, useEffect, useMemo, useRef } from 'react'
import {
  IconAt,
  IconRobotFace,
  IconSparkle2,
  IconSparkleHighlight,
  IconUserCircle
} from '@tabler/icons-react'
import type { Tables } from '@/supabase/types'

import { AssistantIcon } from '@/components/assistants/assistant-icon'
import { useClickOutside } from '@/components/chat/picker-hooks/use-click-outside'
import { useDelayedPaywall } from '@/hooks/use-delayed-paywall'
import { validatePlanForAssistant } from '@/lib/subscription'
import { cn } from '@/lib/utils'

export type UnifiedPlatformMentionOption = {
  kind: 'platform'
  id: 'losi:platform'
  token: 'Losi'
  label: 'Losi'
  description: string
}

export type UnifiedSuperAgentMentionOption = {
  kind: 'super_agent'
  id: string
  token: string
  label: string
  description: string
}

export type UnifiedEveryoneMentionOption = {
  kind: 'everyone'
  id: 'all'
  token: 'Everyone'
  label: 'Everyone'
  description: string
}

export type UnifiedHumanMentionOption = {
  kind: 'human'
  id: string
  token: string
  label: string
  description: string
  imageUrl: string | null
  source: 'group' | 'workspace'
}

export type UnifiedAssistantMentionOption = {
  kind: 'assistant'
  id: string
  token: string
  label: string
  description: string | null
  assistant: Tables<'assistants'>
}

export type UnifiedStaffingAgentMentionOption = {
  kind: 'staffing_agent'
  id: string
  token: string
  label: string
  description: string
  imageUrl: string | null
}

export type UnifiedMentionOption =
  | UnifiedPlatformMentionOption
  | UnifiedSuperAgentMentionOption
  | UnifiedEveryoneMentionOption
  | UnifiedHumanMentionOption
  | UnifiedAssistantMentionOption
  | UnifiedStaffingAgentMentionOption

type UnifiedMentionPickerProps = {
  isOpen: boolean
  searchQuery: string
  options: UnifiedMentionOption[]
  focusSignal: boolean
  profile: Tables<'profiles'> | null
  onOpenChange: (isOpen: boolean) => void
  onSelectMention: (option: UnifiedMentionOption) => void
  /** Secondary: swap host chat to this Assistant (context swap). */
  onUseAssistantAsActive?: (assistant: Tables<'assistants'>) => void
  onRequestPaywall?: () => void
}

const ACTIVE_MENTION_PATTERN = /@\w*$/

export const LOSI_PLATFORM_MENTION_OPTION: UnifiedPlatformMentionOption = {
  kind: 'platform',
  id: 'losi:platform',
  token: 'Losi',
  label: 'Losi',
  description: 'Platform mention'
}

export const LOSI_SUPER_AGENT_MENTION_OPTIONS: readonly UnifiedSuperAgentMentionOption[] = [
  // Sites and Engines are beta and stay hidden from the public @ picker.
]

export const EVERYONE_MENTION_OPTION: UnifiedEveryoneMentionOption = {
  kind: 'everyone',
  id: 'all',
  token: 'Everyone',
  label: 'Everyone',
  description: 'Notify every active member in this group chat'
}

export function toAssistantMentionOption(
  assistant: Tables<'assistants'>
): UnifiedAssistantMentionOption {
  return {
    kind: 'assistant',
    id: assistant.id,
    token: assistant.name,
    label: assistant.name,
    description: assistant.description,
    assistant
  }
}

export function getMentionInsertText(option: UnifiedMentionOption) {
  if (option.kind === 'platform') {
    return '@[Losi](losi:platform)'
  }

  if (option.kind === 'assistant') {
    return `@[${option.label}](assistant:${option.id})`
  }

  if (option.kind === 'super_agent') {
    return `@[${option.label}](super-agent:${option.id})`
  }

  if (option.kind === 'everyone') {
    return '@[Everyone](everyone:all)'
  }

  if (option.kind === 'staffing_agent') {
    return `@[${option.label}](staffing_agent:${option.id})`
  }

  return `@[${option.label}](member:${option.id})`
}

export function replaceActiveMention(
  value: string,
  option: UnifiedMentionOption
) {
  const insertText = `${getMentionInsertText(option)} `

  if (ACTIVE_MENTION_PATTERN.test(value)) {
    return value.replace(ACTIVE_MENTION_PATTERN, insertText)
  }

  return `${value}${value && !value.endsWith(' ') ? ' ' : ''}${insertText}`
}

export function filterUnifiedMentionOptions(
  options: UnifiedMentionOption[],
  query: string
) {
  const normalizedQuery = query.trim().toLowerCase()

  if (!normalizedQuery) {
    return options
  }

  return options.filter((option) => {
    const token = option.token.toLowerCase()
    const label = option.label.toLowerCase()
    const description = option.description?.toLowerCase() ?? ''
    const category = getMentionCategoryLabel(option).toLowerCase()

    return [token, label, description, category].some((value) =>
      value.includes(normalizedQuery)
    )
  })
}

export function getFirstUnifiedMentionOption(
  options: UnifiedMentionOption[],
  query: string
) {
  return filterUnifiedMentionOptions(options, query)[0] ?? null
}

function getInitials(label: string) {
  const parts = label
    .trim()
    .split(/\s+/)
    .filter(Boolean)

  if (parts.length === 0) {
    return '@'
  }

  return parts
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('')
}

function getTypeLabel(option: UnifiedMentionOption) {
  if (option.kind === 'platform') {
    return 'Platform'
  }

  if (option.kind === 'assistant') {
    return 'Assistant'
  }

  if (option.kind === 'super_agent') {
    return 'Super Agents'
  }

  if (option.kind === 'everyone') {
    return 'Group'
  }

  if (option.kind === 'staffing_agent') {
    return 'Agent'
  }

  return option.source === 'group' ? 'Chat member' : 'Workspace member'
}

function getMentionCategoryLabel(option: UnifiedMentionOption) {
  if (option.kind === 'platform') {
    return 'Losi'
  }

  if (option.kind === 'super_agent') {
    return 'Losi Super Agents'
  }

  if (option.kind === 'assistant') {
    return 'Assistants'
  }

  if (option.kind === 'everyone') {
    return 'Group'
  }

  if (option.kind === 'staffing_agent') {
    return 'Staffed agents'
  }

  return option.source === 'group' ? 'Chat members' : 'Workspace members'
}

function getMentionCategoryIcon(option: UnifiedMentionOption) {
  if (option.kind === 'everyone') {
    return <IconAt className="size-3.5" stroke={1.8} />
  }

  if (option.kind === 'super_agent') {
    return <IconSparkleHighlight className="size-3.5" stroke={1.8} />
  }

  if (option.kind === 'assistant') {
    return <IconRobotFace className="size-3.5" stroke={1.8} />
  }

  if (option.kind === 'platform') {
    return <IconSparkle2 className="size-3.5" stroke={1.8} />
  }

  if (option.kind === 'staffing_agent') {
    return <IconRobotFace className="size-3.5" stroke={1.8} />
  }

  return <IconUserCircle className="size-3.5" stroke={1.8} />
}

function groupMentionOptions(options: UnifiedMentionOption[]) {
  const groups = new Map<string, UnifiedMentionOption[]>()

  options.forEach((option) => {
    const category = getMentionCategoryLabel(option)
    groups.set(category, [...(groups.get(category) ?? []), option])
  })

  let startIndex = 0

  return Array.from(groups.entries()).flatMap(([category, groupedOptions]) => {
    const firstOption = groupedOptions[0]

    if (!firstOption) return []

    const group = {
      category,
      icon: getMentionCategoryIcon(firstOption),
      options: groupedOptions,
      startIndex
    }

    startIndex += groupedOptions.length

    return [group]
  })
}

function MentionIcon({ option }: { option: UnifiedMentionOption }) {
  if (option.kind === 'assistant') {
    return (
      <AssistantIcon
        assistant={option.assistant}
        size={28}
        excludeSpecificLogos={false}
      />
    )
  }

  if (option.kind === 'platform') {
    return (
      <span className="border-primary/20 bg-primary/10 text-primary flex size-7 shrink-0 items-center justify-center rounded-lg border">
        <IconSparkle2 className="size-4" stroke={1.8} />
      </span>
    )
  }

  if (option.kind === 'super_agent') {
    return (
      <span className="flex size-7 shrink-0 items-center justify-center rounded-lg border border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300">
        <IconSparkleHighlight className="size-4" stroke={1.8} />
      </span>
    )
  }

  if (option.kind === 'everyone') {
    return (
      <span className="flex size-7 shrink-0 items-center justify-center rounded-lg border border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-300">
        <IconAt className="size-4" stroke={1.8} />
      </span>
    )
  }

  if (option.kind === 'staffing_agent') {
    if (option.imageUrl) {
      return (
        <span
          aria-hidden="true"
          className="size-7 shrink-0 rounded-lg bg-cover bg-center"
          style={{ backgroundImage: `url(${option.imageUrl})` }}
        />
      )
    }

    return (
      <span className="flex size-7 shrink-0 items-center justify-center rounded-lg border border-sky-500/30 bg-sky-500/10 text-sky-700 dark:text-sky-300">
        <IconRobotFace className="size-4" stroke={1.8} />
      </span>
    )
  }

  if (option.imageUrl) {
    return (
      <span
        aria-hidden="true"
        className="size-7 shrink-0 rounded-full bg-cover bg-center"
        style={{ backgroundImage: `url(${option.imageUrl})` }}
      />
    )
  }

  const initials = getInitials(option.label)

  return (
    <span className="border-border bg-muted text-muted-foreground flex size-7 shrink-0 items-center justify-center rounded-full border text-[11px] font-semibold">
      {initials === '@' ? <IconUserCircle className="size-4" /> : initials}
    </span>
  )
}

export function UnifiedMentionPicker({
  isOpen,
  searchQuery,
  options,
  focusSignal,
  profile,
  onOpenChange,
  onSelectMention,
  onUseAssistantAsActive,
  onRequestPaywall
}: UnifiedMentionPickerProps) {
  const itemsRef = useRef<(HTMLElement | null)[]>([])
  const scheduleUpgradePaywall = useDelayedPaywall(onRequestPaywall)
  const filteredOptions = useMemo(
    () => filterUnifiedMentionOptions(options, searchQuery),
    [options, searchQuery]
  )
  const groupedOptions = useMemo(
    () => groupMentionOptions(filteredOptions),
    [filteredOptions]
  )

  const focusItem = useCallback((index: number) => {
    const item = itemsRef.current[index]

    if (!item) return

    item.focus()
    item.scrollIntoView({ block: 'nearest' })
  }, [])

  useClickOutside(itemsRef, () => onOpenChange(false))

  useEffect(() => {
    itemsRef.current = itemsRef.current.slice(0, filteredOptions.length)

    if (isOpen && itemsRef.current[0]) {
      focusItem(0)
    }
  }, [filteredOptions.length, focusItem, focusSignal, isOpen])

  const selectOption = (option: UnifiedMentionOption) => {
    if (
      option.kind === 'assistant' &&
      !validatePlanForAssistant(profile, option.assistant)
    ) {
      scheduleUpgradePaywall()
      return
    }

    onSelectMention(option)
    onOpenChange(false)
  }

  const getKeyDownHandler =
    (index: number) => (event: React.KeyboardEvent<HTMLButtonElement>) => {
      if (event.key === 'Backspace') {
        event.preventDefault()
        onOpenChange(false)
        return
      }

      if (event.key === 'Enter') {
        event.preventDefault()
        const option = filteredOptions[index]

        if (option) {
          selectOption(option)
        }

        return
      }

      if (event.key === 'Escape') {
        event.preventDefault()
        onOpenChange(false)
        return
      }

      if (
        (event.key === 'Tab' || event.key === 'ArrowDown') &&
        !event.shiftKey &&
        index === filteredOptions.length - 1
      ) {
        event.preventDefault()
        focusItem(0)
        return
      }

      if (event.key === 'ArrowUp' && !event.shiftKey && index === 0) {
        event.preventDefault()
        focusItem(itemsRef.current.length - 1)
        return
      }

      if (event.key === 'ArrowUp') {
        event.preventDefault()
        const previousIndex =
          index - 1 >= 0 ? index - 1 : itemsRef.current.length - 1
        focusItem(previousIndex)
        return
      }

      if (event.key === 'ArrowDown') {
        event.preventDefault()
        const nextIndex = index + 1 < itemsRef.current.length ? index + 1 : 0
        focusItem(nextIndex)
      }
    }

  if (!isOpen) {
    return null
  }

  return (
    <div className="flex w-full min-w-0 max-w-full flex-col overflow-hidden p-2 text-sm shadow-lg">
      <div className="text-muted-foreground flex min-w-0 items-center gap-2 px-2 pb-1 text-xs font-medium">
        <IconAt className="size-3.5 shrink-0" stroke={1.8} />
        <span className="truncate">Mentions</span>
      </div>

      {filteredOptions.length === 0 ? (
        <div className="text-muted-foreground flex h-14 items-center justify-center text-sm italic">
          No matching mentions.
        </div>
      ) : (
        <div className="max-h-80 w-full min-w-0 scroll-py-2 overflow-y-auto overscroll-contain pr-1">
          {groupedOptions.map((group) => {
            return (
              <div key={group.category} className="py-1">
                <div className="text-muted-foreground flex min-w-0 items-center gap-1.5 px-2 py-1 text-[11px] font-medium uppercase tracking-normal">
                  {group.icon}
                  <span className="min-w-0 truncate">{group.category}</span>
                  <span className="text-muted-foreground/70 shrink-0">{group.options.length}</span>
                </div>
                {group.options.map((option, optionIndex) => {
                  const index = group.startIndex + optionIndex

                  return (
                    <button
                      key={`${option.kind}:${option.id}`}
                      ref={(ref) => {
                        itemsRef.current[index] = ref
                      }}
                      type="button"
                      className={cn(
                        'hover:bg-accent focus:bg-accent grid w-full min-w-0 cursor-pointer grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-3 overflow-hidden rounded-lg p-2 text-left focus:outline-none',
                        option.kind === 'platform' && 'bg-primary/5'
                      )}
                      onClick={() => selectOption(option)}
                      onKeyDown={getKeyDownHandler(index)}
                    >
                      <MentionIcon option={option} />
                      <div className="min-w-0 max-w-full overflow-hidden">
                        <div className="flex min-w-0 items-center gap-2">
                          <span
                            className="min-w-0 flex-1 truncate font-semibold"
                            title={option.label}
                          >
                            {option.label}
                          </span>
                          <span className="border-border text-muted-foreground max-w-[45%] shrink-0 truncate rounded border px-1.5 py-0.5 text-[10px] uppercase tracking-normal">
                            {getTypeLabel(option)}
                          </span>
                        </div>
                        <div
                          className="text-muted-foreground w-full min-w-0 truncate text-xs"
                          title={option.description ?? undefined}
                        >
                          {option.description}
                        </div>
                      </div>
                      {option.kind === 'assistant' && onUseAssistantAsActive ? (
                        <span
                          role="button"
                          tabIndex={0}
                          className="text-muted-foreground hover:text-foreground shrink-0 text-xs underline"
                          onClick={e => {
                            e.stopPropagation()
                            if (
                              !validatePlanForAssistant(profile, option.assistant)
                            ) {
                              scheduleUpgradePaywall()
                              return
                            }
                            onUseAssistantAsActive(option.assistant)
                            onOpenChange(false)
                          }}
                          onKeyDown={e => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault()
                              e.stopPropagation()
                              if (
                                !validatePlanForAssistant(
                                  profile,
                                  option.assistant
                                )
                              ) {
                                scheduleUpgradePaywall()
                                return
                              }
                              onUseAssistantAsActive(option.assistant)
                              onOpenChange(false)
                            }
                          }}
                        >
                          Use as active
                        </span>
                      ) : null}
                    </button>
                  )
                })}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
